import * as angular from 'angular';
import {IController} from 'angular';
import * as _ from 'lodash';
import {AgentAccountAssignmentDialogService} from '../../services/agent-account-assignment-dialog.service';
import {BatchRequestsService} from '../../../batch/services/batch-requests.service';
import {BatchDialogService} from '../../../batch/services/batch-dialogs-service.service';
import {BringozLoaderService} from '../../../commons/services/bringoz-loader.service';
import {ConfirmDialogService} from '../../../commons/services/confirm-dialog.service';
import {ConfirmChangeStateService} from '../../../commons/services/confirm-change-state.service';
import {AccountsAlerts} from '../../services/accounts-alerts.service';

export interface AgentAssignmentImportType {
  id: string;
  label: string;
  entity: string;
  importerPath: string;
  permissions: string[];
}

/**
 * Row as returned by /{importerPath}/validate - ImportVerification<AccountPreferredAgent>.
 */
export interface AgentAssignmentImportRow {
  rowNumber?: number;
  entity?: {
    accountId?: string;
    agentId?: string;
    serviceType?: string;
    priority?: number;
    restricted?: boolean;
    dedicated?: boolean;
  };
  mappingErrors?: Array<AgentAssignmentMappingError | string>;
  rowId?: string;
  errorsDisplay?: string;
  hasErrors?: boolean;
}

export interface AgentAssignmentMappingError {
  type?: string;
  description?: string;
  message?: string;
  fieldName?: string;
}

export class AgentAssignmentsImportViewCtrl implements IController {

  importTypeId: string;

  public importType: AgentAssignmentImportType;
  public rows: AgentAssignmentImportRow[] = [];
  public visibleRows: AgentAssignmentImportRow[] = [];
  public totals: {total: number, errors: number} = {total: 0, errors: 0};
  public currentNavItem: string = 'total';
  public uploadComplete: boolean = false;
  public isFieldMappingOpen: boolean = false;
  public isLoading: boolean = false;
  public schemas: any[] = [];
  public schemaDefaultFields: any[] = [];

  /* @ngInject */
  constructor(
    private $state,
    private $log,
    private $q,
    private UUIDService,
    private accountsConstants,
    private agentAccountAssignmentDialogService: AgentAccountAssignmentDialogService,
    private batchRequestsService: BatchRequestsService,
    private batchDialogService: BatchDialogService,
    private bringozLoaderService: BringozLoaderService,
    private confirmDialogService: ConfirmDialogService,
    private confirmChangeStateService: ConfirmChangeStateService,
    private accountsAlerts: AccountsAlerts
  ) {
  }

  $onInit() {
    this.importType = _.find(this.accountsConstants.agentAssignmentImportTypes,
      (type: AgentAssignmentImportType) => type.id === this.importTypeId);

    if (!this.importType) {
      this.$log.error('Unknown agent assignment import type', this.importTypeId);
      return;
    }

    this._loadSchemas();
  }

  changeNavItem(item: string) {
    this.currentNavItem = item;
    this._applyRows(this.rows);
  }

  onUpload(event: Event) {
    this.agentAccountAssignmentDialogService
      .showUploadAgentAssignmentsDialog(event, this.importType, this.schemas)
      .then((rows) => {
        this.currentNavItem = 'total';
        this._applyRows(_.map(rows || [], (row) => this._enrichRow(row)));
        this.confirmChangeStateService.setIsEnabled(this.uploadComplete);
      }, () => {
      });
  }

  onRemoveRow(row: AgentAssignmentImportRow) {
    this._applyRows(_.reject(this.rows, (r) => r.rowId === row.rowId));

    if (!this.rows.length) {
      this.currentNavItem = 'total';
      this.confirmChangeStateService.reset();
    }
  }

  onCancel(event: Event) {
    return this.confirmDialogService
      .confirm('Cancel Upload', 'Are you sure you want to cancel the upload?', event)
      .then(() => {
        this._reset();
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onImport(event: Event) {
    // Rows the server flagged cannot be imported, so only clean rows are sent.
    const items = _.chain(this.rows)
      .reject((row) => row.hasErrors)
      .map((row) => _.omit(row, ['rowId', 'errorsDisplay', 'hasErrors']))
      .value();

    if (!items.length) {
      return;
    }

    this.bringozLoaderService.on();

    this.batchRequestsService.createAgentAssignmentsBatch(this.importType.importerPath, items)
      .then((batch) => {
        this.bringozLoaderService.off();
        return this.batchDialogService.batchAgentAssignmentsProgress(
          event, batch, this.importType.importerPath, this.importType.label
        );
      })
      .then((errorItems: any) => {
        this._applyBatchErrors(errorItems || []);
      })
      .catch((error) => {
        if (error) {
          this.accountsAlerts.importAgentAssignmentsError(error);
          this.$log.error('Failed to import agent assignments', error);
        } else {
          this.$log.info('Dialog Closed');
        }
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
  }

  onBackToAssignments() {
    this.confirmChangeStateService.reset();
    this.$state.go('accounts-agent-assignments');
  }

  onOpenFieldMappingPanel() {
    this.isFieldMappingOpen = true;
  }

  onCloseFieldMappingPanel() {
    this.isFieldMappingOpen = false;
  }

  onSchemaCreated(schema) {
    this.schemas = [...this.schemas, schema];
  }

  onSchemaUpdated(schema) {
    this.schemas = _.map(this.schemas, (s) => s.id === schema.id ? schema : s);
  }

  onSchemaDeleted(schemaId) {
    this.schemas = _.reject(this.schemas, (s) => s.id === schemaId);
  }

  onDownloadExample() {
    const downloadUrl = this.accountsConstants.importAgentsSampleFileLink;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = "import-accounts-agents-example.xls";
    document.body.appendChild(a);
    a.click();
  }

  // ******************************
  //        Private methods
  // ******************************

  private _loadSchemas() {
    this.isLoading = true;

    // batchRequestsService returns native promises - $q.all adopts them so the results
    // land inside a digest. A failure must not block uploading, it only means no saved
    // mapping can be picked, hence the per-call catch.
    this.$q.all([
      this.$q.when(this.batchRequestsService.getSchemas(this.importType.entity)).catch(() => []),
      this.$q.when(this.batchRequestsService.getSchemaDefaultFields(this.importType.entity)).catch(() => [])
    ])
      .then(([schemas, schemaDefaultFields]: any[]) => {
        this.schemas = schemas || [];
        this.schemaDefaultFields = schemaDefaultFields || [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _applyRows(rows: AgentAssignmentImportRow[]) {
    this.rows = rows;
    this.totals = this._getTotals(rows);
    this.uploadComplete = !!rows.length;
    this.visibleRows = this.currentNavItem === 'errors'
      ? _.filter(rows, (row) => row.hasErrors)
      : rows;
  }

  private _enrichRow(row: AgentAssignmentImportRow): AgentAssignmentImportRow {
    const mappingErrors = row.mappingErrors || [];

    return {
      ...row,
      rowId: this.UUIDService.generate(),
      hasErrors: _.some(mappingErrors, (error) => this._isBlockingError(error)),
      errorsDisplay: this._formatErrors(mappingErrors)
    };
  }

  private _formatErrors(mappingErrors: Array<AgentAssignmentMappingError | string>): string {
    const messages = _.chain(mappingErrors)
      .map((error) => _.isString(error) ? error : (error?.description || error?.message || error?.fieldName))
      .compact()
      .uniq()
      .value();

    return messages.length ? messages.join(', ') : '-';
  }

  private _isBlockingError(error: AgentAssignmentMappingError | string): boolean {
    return _.isString(error) || !error.type || error.type === 'Error';
  }

  private _getTotals(rows: AgentAssignmentImportRow[]) {
    return {
      total: rows.length,
      errors: _.filter(rows, (row) => row.hasErrors).length
    };
  }

  private _applyBatchErrors(errorItems: any[]) {
    if (!errorItems.length) {
      this.accountsAlerts.importAgentAssignmentsSuccess();
      this._reset();
      this.onBackToAssignments();
      return;
    }

    const errorRowNumbers = _.map(errorItems, 'rowNumber');

    const failedRows = _.chain(this.rows)
      .filter((row) => _.includes(errorRowNumbers, row.rowNumber))
      .map((row) => {
        const errorItem = _.find(errorItems, (item) => item.rowNumber === row.rowNumber);
        return this._enrichRow({
          ...row,
          mappingErrors: _.get(errorItem, 'mappingErrors', [])
        });
      })
      .value();

    this.currentNavItem = 'errors';
    this._applyRows(failedRows);
  }

  private _reset() {
    this.currentNavItem = 'total';
    this._applyRows([]);
    this.confirmChangeStateService.reset();
  }
}

angular
  .module('bringoz.admin.accounts')
  .component('agentAssignmentsImportView', {
    bindings: {
      importTypeId: '<'
    },
    template: require('./agent-assignments-import-view.html').default,
    controller: AgentAssignmentsImportViewCtrl
  });
